function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Console display for use in the Iris environment.
 */
import React, { PureComponent } from 'react';
import { ContextActions } from '@deephaven/components';
import { vsCheck } from '@deephaven/icons';
import classNames from 'classnames';
import memoize from 'memoize-one';
import throttle from 'lodash.throttle';
import Log from '@deephaven/log';
import { assertNotNull, Pending, PromiseUtils } from '@deephaven/utils';
import ConsoleHistory from "./console-history/ConsoleHistory.js";
import SHORTCUTS from "./ConsoleShortcuts.js";
import LogLevel from "./log/LogLevel.js";
import ConsoleInput from "./ConsoleInput.js";
import CsvOverlay from "./csv/CsvOverlay.js";
import CsvInputBar from "./csv/CsvInputBar.js";
import "./Console.css";
import ConsoleStatusBar from "./ConsoleStatusBar.js";
import ConsoleObjectsMenu from "./ConsoleObjectsMenu.js";
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('Console');
var DEFAULT_SETTINGS = {
  isAutoLaunchPanelsEnabled: true,
  isPrintStdOutEnabled: true,
  isClosePanelsOnDisconnectEnabled: true
};
function defaultSupportsType() {
  return true;
}
function defaultIconForType(type) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return /*#__PURE__*/_jsx(_Fragment, {});
}
export class Console extends PureComponent {
  /**
   * Check if the provided log level is an error type
   * @param logLevel The LogLevel being checked
   * @returns true if the log level is an error level log
   */
  static isErrorLevel(logLevel) {
    return logLevel === LogLevel.STDERR || logLevel === LogLevel.ERROR || logLevel === LogLevel.FATAL;
  }

  /**
   * Check if the provided log level is output level
   * @param logLevel The LogLevel being checked
   * @returns true if the log level should be output to the console
   */
  static isOutputLevel(logLevel) {
    // We want all errors to be output, in addition to STDOUT.
    // That way the user is more likely to see them.
    return logLevel === LogLevel.STDOUT || Console.isErrorLevel(logLevel);
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "cancelListener", void 0);
    _defineProperty(this, "consolePane", void 0);
    _defineProperty(this, "consoleInput", void 0);
    _defineProperty(this, "consoleHistoryScrollPane", void 0);
    _defineProperty(this, "consoleHistoryContent", void 0);
    _defineProperty(this, "pending", void 0);
    _defineProperty(this, "queuedLogMessages", void 0);
    _defineProperty(this, "resizeObserver", void 0);
    _defineProperty(this, "processLogMessageQueue", throttle(() => {
      this.setState(state => {
        log.debug2('processLogMessageQueue', this.queuedLogMessages.length, ' items');
        var consoleHistory = state.consoleHistory;
        consoleHistory = consoleHistory.concat(this.queuedLogMessages);
        this.queuedLogMessages = [];
        return {
          consoleHistory
        };
      });
    }, Console.LOG_THROTTLE));
    _defineProperty(this, "getObjects", memoize(objectMap => Array.from(objectMap.values())));
    _defineProperty(this, "getContextActions", memoize(actions => [...actions, {
      action: this.handleClearShortcut,
      shortcut: SHORTCUTS.CONSOLE.CLEAR
    }, {
      action: this.handleFocusHistory,
      shortcut: SHORTCUTS.CONSOLE.FOCUS_HISTORY
    }]));
    this.handleCommandResult = this.handleCommandResult.bind(this);
    this.handleCommandStarted = this.handleCommandStarted.bind(this);
    this.handleCommandSubmit = this.handleCommandSubmit.bind(this);
    this.handleClearShortcut = this.handleClearShortcut.bind(this);
    this.handleFocusHistory = this.handleFocusHistory.bind(this);
    this.handleLogMessage = this.handleLogMessage.bind(this);
    this.handleOverflowActions = this.handleOverflowActions.bind(this);
    this.handleScrollPaneScroll = this.handleScrollPaneScroll.bind(this);
    this.handleHistoryResize = this.handleHistoryResize.bind(this);
    this.handleToggleAutoLaunchPanels = this.handleToggleAutoLaunchPanels.bind(this);
    this.handleToggleClosePanelsOnDisconnect = this.handleToggleClosePanelsOnDisconnect.bind(this);
    this.handleTogglePrintStdout = this.handleTogglePrintStdout.bind(this);
    this.handleUploadCsv = this.handleUploadCsv.bind(this);
    this.handleHideCsv = this.handleHideCsv.bind(this);
    this.handleCsvFileCanceled = this.handleCsvFileCanceled.bind(this);
    this.handleCsvFileOpened = this.handleCsvFileOpened.bind(this);
    this.handleCsvPaste = this.handleCsvPaste.bind(this);
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleClearDragError = this.handleClearDragError.bind(this);
    this.handleOpenCsvTable = this.handleOpenCsvTable.bind(this);
    this.handleCsvUpdate = this.handleCsvUpdate.bind(this);
    this.handleCsvError = this.handleCsvError.bind(this);
    this.handleCsvInProgress = this.handleCsvInProgress.bind(this);
    this.consolePane = /*#__PURE__*/React.createRef();
    this.consoleInput = /*#__PURE__*/React.createRef();
    this.consoleHistoryScrollPane = /*#__PURE__*/React.createRef();
    this.consoleHistoryContent = /*#__PURE__*/React.createRef();
    this.pending = new Pending();
    this.queuedLogMessages = [];
    this.resizeObserver = new window.ResizeObserver(this.handleHistoryResize);
    var _this$props = this.props,
      _objectMap = _this$props.objectMap,
      settings = _this$props.settings;
    this.state = _objectSpread(_objectSpread({
      // Need separate histories as console history has stdout/stderr output
      consoleHistory: [],
      // Height of the viewport of the console input and history
      consoleHeight: 0,
      isScrollDecorationShown: false,
      // Location of objects in the console history
      objectHistoryMap: new Map(),
      // The object definitions, name/type
      objectMap: new Map(_objectMap),
      showCsvOverlay: false,
      csvFile: null,
      csvPaste: null,
      dragError: null,
      csvUploadInProgress: false,
      isStuckToBottom: true
    }, DEFAULT_SETTINGS), settings);
  }
  componentDidMount() {
    this.initConsoleLogging();
    var _this$props2 = this.props,
      dh = _this$props2.dh,
      session = _this$props2.session;
    session.addEventListener(dh.IdeSession.EVENT_COMMANDSTARTED, this.handleCommandStarted);
    this.updateDimensions();
    if (this.consoleHistoryScrollPane.current && this.consoleHistoryContent.current) {
      this.resizeObserver.observe(this.consoleHistoryScrollPane.current);
      this.resizeObserver.observe(this.consoleHistoryContent.current);
    }
  }
  componentDidUpdate(prevProps, prevState) {
    var props = this.props,
      state = this.state;
    this.sendSettingsChange(prevState, state);
    if (props.objectMap !== prevProps.objectMap) {
      this.updateObjectMap();
    }
    if (state.isStuckToBottom && !this.isAtBottom()) {
      this.scrollConsoleHistoryToBottom();
    }
  }
  componentWillUnmount() {
    var _this$props3 = this.props,
      dh = _this$props3.dh,
      session = _this$props3.session;
    session.removeEventListener(dh.IdeSession.EVENT_COMMANDSTARTED, this.handleCommandStarted);
    this.pending.cancel();
    this.processLogMessageQueue.cancel();
    this.deinitConsoleLogging();
    this.resizeObserver.disconnect();
  }
  initConsoleLogging() {
    var session = this.props.session;
    this.cancelListener = session.onLogMessage(this.handleLogMessage);
  }
  deinitConsoleLogging() {
    if (this.cancelListener != null) {
      this.cancelListener();
      this.cancelListener = undefined;
    }
  }
  isAtBottom() {
    if (this.consoleHistoryScrollPane.current == null) {
      return false;
    }
    var _this$consoleHistoryS = this.consoleHistoryScrollPane.current,
      scrollHeight = _this$consoleHistoryS.scrollHeight,
      clientHeight = _this$consoleHistoryS.clientHeight,
      scrollTop = _this$consoleHistoryS.scrollTop;
    return Math.abs(scrollHeight - clientHeight - scrollTop) <= 1;
  }
  handleClearShortcut(event) {
    var _this$consoleInput$cu;
    event.preventDefault();
    event.stopPropagation();
    (_this$consoleInput$cu = this.consoleInput.current) === null || _this$consoleInput$cu === void 0 || _this$consoleInput$cu.clear();
  }
  handleCommandStarted(event) {
    var _event$detail = event.detail,
      code = _event$detail.code,
      result = _event$detail.result;
    var wrappedResult = this.pending.add(result);
    var historyItem = {
      command: code,
      disabledObjects: [],
      startTime: Date.now(),
      cancelResult: () => {
        result.cancel();
      },
      wrappedResult
    };
    var _this$props4 = this.props,
      commandHistoryStorage = _this$props4.commandHistoryStorage,
      language = _this$props4.language,
      scope = _this$props4.scope;
    var workspaceItemPromise = commandHistoryStorage.addItem(language, scope, code, {
      command: code,
      startTime: new Date().toJSON()
    });
    workspaceItemPromise.catch(err => {
      log.debug('Error adding workspace item', err);
    });
    this.setState(state => ({
      consoleHistory: state.consoleHistory.concat(historyItem)
    }), () => {
      this.scrollConsoleHistoryToBottom();
    });
    wrappedResult.then(resolved => {
      this.handleCommandResult(resolved, historyItem, workspaceItemPromise);
    }).catch(error => {
      this.handleCommandError(error, historyItem, workspaceItemPromise);
    });
  }
  handleCommandResult(result, historyItem, workspaceItemPromise) {
    var _result$startTimestam, _result$endTimestamp;
    var serverStartTime = result === null || result === void 0 || (_result$startTimestam = result.startTimestamp) === null || _result$startTimestam === void 0 ? void 0 : _result$startTimestam.asNumber();
    var serverEndTime = result === null || result === void 0 || (_result$endTimestamp = result.endTimestamp) === null || _result$endTimestamp === void 0 ? void 0 : _result$endTimestamp.asNumber();
    var newHistoryItem = _objectSpread(_objectSpread({}, historyItem), {}, {
      wrappedResult: undefined,
      cancelResult: undefined,
      result: result !== null && result !== void 0 ? result : historyItem.result,
      serverStartTime,
      serverEndTime
    });
    this.setState(_ref => {
      var consoleHistory = _ref.consoleHistory;
      var itemIndex = consoleHistory.lastIndexOf(historyItem);
      if (itemIndex < 0) {
        log.error("historyItem not found in consoleHistory");
        return null;
      }
      var newHistory = consoleHistory.concat();
      newHistory[itemIndex] = newHistoryItem;
      return {
        consoleHistory: newHistory
      };
    });
    if (!result) {
      return;
    }
    this.scrollConsoleHistoryToBottom();
    this.updateHistory(result, newHistoryItem);
    this.updateKnownObjects(newHistoryItem);
    this.updateWorkspaceHistoryItem({
      error: result.error,
      serverStartTime,
      serverEndTime
    }, workspaceItemPromise);
    this.closeRemovedItems(result.changes);
    this.openUpdatedItems(result.changes);
  }
  handleCommandError(error, historyItem, workspaceItemPromise) {
    if (PromiseUtils.isCanceled(error)) {
      log.debug('Called handleCommandError on a cancelled promise result');
      return;
    }
    this.updateWorkspaceHistoryItem({
      error: "".concat(error)
    }, workspaceItemPromise);
    this.setState(state => {
      var history = state.consoleHistory.concat();
      var index = history.lastIndexOf(historyItem);
      var newHistoryItem = _objectSpread({}, historyItem);
      newHistoryItem.wrappedResult = undefined;
      newHistoryItem.cancelResult = undefined;
      newHistoryItem.endTime = Date.now();
      newHistoryItem.result = {
        error
      };
      history[index] = newHistoryItem;
      return {
        consoleHistory: history
      };
    });
  }
  handleFocusHistory(event) {
    event.preventDefault();
    event.stopPropagation();
    var focusCommandHistory = this.props.focusCommandHistory;
    focusCommandHistory();
  }
  handleLogMessage(message) {
    var isPrintStdOutEnabled = this.state.isPrintStdOutEnabled;
    if (!isPrintStdOutEnabled) {
      return;
    }
    if (Console.isOutputLevel(message.logLevel)) {
      this.queueLogMessage(message.message, message.logLevel);
    }
  }
  queueLogMessage(message, logLevel) {
    var result = {};
    if (Console.isErrorLevel(logLevel)) {
      result.error = message;
    } else {
      result.message = message;
    }
    var historyItem = {
      command: undefined,
      result
    };
    this.queuedLogMessages.push(historyItem);
    this.processLogMessageQueue();
  }
  openUpdatedItems(changes) {
    log.debug('openUpdatedItems', changes);
    var isAutoLaunchPanelsEnabled = this.state.isAutoLaunchPanelsEnabled;
    if (changes == null) {
      return;
    }
    var openObject = this.props.openObject;
    [...changes.created, ...changes.updated].forEach(object => openObject(object, isAutoLaunchPanelsEnabled && (object.title === undefined || !object.title.startsWith('_'))));
  }
  closeRemovedItems(changes) {
    if (changes == null || changes.removed == null || changes.removed.length === 0) {
      return;
    }
    var closeObject = this.props.closeObject;
    var removed = changes.removed;
    removed.forEach(object => closeObject(object));
  }
  updateHistory(result, historyItemParam) {
    var historyItem = historyItemParam;
    if (result == null || result.changes == null || historyItem == null) {
      return;
    }
    historyItem.endTime = Date.now();

    // Update history to re-render items as necessary
    this.setState(state => {
      var consoleHistory = state.consoleHistory.concat();
      return {
        consoleHistory
      };
    });
  }
  updateKnownObjects(historyItem) {
    var changes;
    if (historyItem.result) {
      changes = historyItem.result.changes;
    }
    if (!changes || (changes.created == null || changes.created.length === 0) && (changes.updated == null || changes.updated.length === 0) && (changes.removed == null || changes.removed.length === 0)) {
      log.debug2('updateKnownObjects no changes');
      return;
    }
    this.setState(state => {
      var _changes, _changes2, _changes3;
      var consoleHistory = state.consoleHistory;
      var itemIndex = consoleHistory.lastIndexOf(historyItem);
      if (itemIndex < 0) {
        log.error("historyItem not found in state.consoleHistory");
        return null;
      }
      var objectHistoryMap = new Map(state.objectHistoryMap);
      var objectMap = new Map(state.objectMap);
      var disableOldObject = function disableOldObject(object) {
        var isRemoved = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
        var title = object.title;
        assertNotNull(title);
        var oldIndex = objectHistoryMap.get(title);
        // oldIndex can be -1 if a object is active but doesn't have a command in consoleHistory
        // this can happen after clearing the console using 'clear' or 'cls' command

        if (oldIndex != null && oldIndex >= 0) {
          var _consoleHistory$oldIn;
          // disable outdated object variable in the old consoleHistory item
          if (consoleHistory === state.consoleHistory) {
            // First item in the history being updated,
            consoleHistory = consoleHistory.concat();
          }
          var newHistoryItem = _objectSpread(_objectSpread({}, consoleHistory[oldIndex]), {}, {
            disabledObjects: [...((_consoleHistory$oldIn = consoleHistory[oldIndex].disabledObjects) !== null && _consoleHistory$oldIn !== void 0 ? _consoleHistory$oldIn : []), title]
          });
          consoleHistory[oldIndex] = newHistoryItem;
        }
        objectHistoryMap.set(title, itemIndex);
        if (isRemoved) {
          objectMap.delete(title);
        } else {
          objectMap.set(title, object);
        }
      };
      (_changes = changes) === null || _changes === void 0 || _changes.updated.forEach(object => disableOldObject(object));
      (_changes2 = changes) === null || _changes2 === void 0 || _changes2.removed.forEach(object => disableOldObject(object, true));

      // Created objects have to be processed after removed
      // in case the same object name is present in both removed and created
      (_changes3 = changes) === null || _changes3 === void 0 || _changes3.created.forEach(object => {
        var title = object.title;
        assertNotNull(title);
        objectHistoryMap.set(title, itemIndex);
        objectMap.set(title, object);
      });
      return {
        objectHistoryMap,
        objectMap,
        consoleHistory
      };
    });
  }
  updateObjectMap() {
    var objectMap = this.props.objectMap;
    this.setState({
      objectMap
    });
  }

  /**
   * Updates an existing workspace CommandHistoryItem
   * @param result The result to store with the history item.
   * Possibly contains an error message if there was a failure, server start time, and server end time.
   * @param workspaceItemPromise The workspace data row promise for the workspace item to be updated
   */
  updateWorkspaceHistoryItem(result, workspaceItemPromise) {
    var promise = this.pending.add(workspaceItemPromise);
    var endTime = new Date().toJSON();
    promise.then(workspaceItem => {
      var updatedItem = _objectSpread(_objectSpread({}, workspaceItem), {}, {
        data: _objectSpread(_objectSpread({}, workspaceItem.data), {}, {
          endTime,
          result
        })
      });
      var _this$props5 = this.props,
        commandHistoryStorage = _this$props5.commandHistoryStorage,
        language = _this$props5.language;
      commandHistoryStorage.updateItem(language, updatedItem).catch(err => {
        log.warn('Error updating command history storage', err);
      });
    }).catch(err => {
      log.debug('Error updating workspace history item', err);
    });
  }
  scrollConsoleHistoryToBottom() {
    var pane = this.consoleHistoryScrollPane.current;
    if (pane == null) {
      return;
    }
    window.requestAnimationFrame(() => {
      pane.scrollTo({
        top: pane.scrollHeight,
        behavior: 'instant'
      });
    });
  }
  handleScrollPaneScroll(event) {
    log.debug('handleScrollPaneScroll', event);
    var scrollPane = this.consoleHistoryScrollPane.current;
    assertNotNull(scrollPane);
    this.setState({
      isScrollDecorationShown: scrollPane.scrollTop > 0 && scrollPane.scrollHeight > scrollPane.clientHeight,
      isStuckToBottom: this.isAtBottom()
    });
  }
  handleHistoryResize(entries) {
    log.debug('handleHistoryResize', entries);
    var entry = entries[0];
    if (entry.contentRect.height > 0 && entry.contentRect.width > 0) {
      var isStuckToBottom = this.state.isStuckToBottom;
      if (isStuckToBottom && !this.isAtBottom()) {
        this.scrollConsoleHistoryToBottom();
      }
    }
  }
  handleToggleAutoLaunchPanels() {
    this.setState(state => ({
      isAutoLaunchPanelsEnabled: !state.isAutoLaunchPanelsEnabled
    }));
  }
  handleToggleClosePanelsOnDisconnect() {
    this.setState(state => ({
      isClosePanelsOnDisconnectEnabled: !state.isClosePanelsOnDisconnectEnabled
    }));
  }
  handleTogglePrintStdout() {
    this.setState(state => ({
      isPrintStdOutEnabled: !state.isPrintStdOutEnabled
    }));
  }
  handleUploadCsv() {
    this.setState({
      showCsvOverlay: true,
      dragError: null,
      csvUploadInProgress: false
    });
  }
  handleHideCsv() {
    this.setState({
      showCsvOverlay: false,
      csvFile: null,
      csvPaste: null,
      dragError: null,
      csvUploadInProgress: false
    });
  }
  handleCsvFileCanceled() {
    this.setState({
      csvFile: null,
      csvPaste: null
    });
  }
  handleCsvFileOpened(file) {
    this.setState({
      csvFile: file,
      csvPaste: null
    });
  }
  handleCsvPaste(value) {
    this.setState({
      csvFile: null,
      csvPaste: value
    });
  }
  handleDragEnter(e) {
    if (e.dataTransfer == null || e.dataTransfer.items == null || e.dataTransfer.items.length === 0 || e.dataTransfer.items[0].kind === 'string') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      showCsvOverlay: true
    });
    var items = e.dataTransfer.items;
    if (items.length > 1) {
      this.setState({
        dragError: CsvOverlay.MULTIPLE_FILE_ERROR
      });
      return;
    }
    var item = items[0];
    if (CsvOverlay.isValidDropItem(item)) {
      this.setState({
        dragError: null
      });
    } else {
      this.setState({
        dragError: CsvOverlay.FILE_TYPE_ERROR
      });
    }
  }
  handleDragLeave(e) {
    // DragLeave gets fired for every child element, so make sure we're actually leaving the drop zone
    if (e.currentTarget == null || e.currentTarget instanceof Element && e.relatedTarget instanceof Element && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      showCsvOverlay: false,
      dragError: null
    });
  }
  handleClearDragError() {
    this.setState({
      dragError: null
    });
  }
  handleOpenCsvTable(title) {
    var _this$props6 = this.props,
      dh = _this$props6.dh,
      openObject = _this$props6.openObject,
      commandHistoryStorage = _this$props6.commandHistoryStorage,
      language = _this$props6.language,
      scope = _this$props6.scope;
    var _this$state = this.state,
      consoleHistory = _this$state.consoleHistory,
      objectMap = _this$state.objectMap;
    var object = {
      name: title,
      title,
      type: dh.VariableType.TABLE
    };
    var isExistingObject = objectMap.has(title);
    var historyItem = {
      startTime: Date.now(),
      endTime: Date.now(),
      disabledObjects: [],
      result: {
        changes: {
          created: [],
          updated: [],
          removed: [],
          [isExistingObject ? 'updated' : 'created']: [object]
        }
      }
    };
    var history = consoleHistory.concat(historyItem);
    this.setState({
      consoleHistory: history
    });
    this.scrollConsoleHistoryToBottom();
    this.updateKnownObjects(historyItem);
    openObject({
      name: title,
      type: dh.VariableType.TABLE
    });
    commandHistoryStorage.addItem(language, scope, title, {
      command: title,
      startTime: new Date().toJSON(),
      endTime: new Date().toJSON()
    });
  }
  addConsoleHistoryMessage(message, error) {
    var consoleHistory = this.state.consoleHistory;
    var historyItem = {
      startTime: Date.now(),
      endTime: Date.now(),
      result: {
        message,
        error
      }
    };
    var history = consoleHistory.concat(historyItem);
    this.setState({
      consoleHistory: history
    });
  }
  handleCsvUpdate(message) {
    this.addConsoleHistoryMessage(message);
  }
  handleCsvError(error) {
    this.addConsoleHistoryMessage(undefined, error != null ? "".concat(error) : undefined);
  }
  handleCsvInProgress(csvUploadInProgress) {
    this.setState({
      csvUploadInProgress
    });
  }
  handleOverflowActions() {
    var _this$state2 = this.state,
      isAutoLaunchPanelsEnabled = _this$state2.isAutoLaunchPanelsEnabled,
      isClosePanelsOnDisconnectEnabled = _this$state2.isClosePanelsOnDisconnectEnabled,
      isPrintStdOutEnabled = _this$state2.isPrintStdOutEnabled;
    var actions = this.props.actions;
    return [...actions, {
      title: 'Print Stdout',
      action: this.handleTogglePrintStdout,
      group: ContextActions.groups.high,
      icon: isPrintStdOutEnabled ? vsCheck : undefined,
      order: 10
    }, {
      title: 'Auto Launch Panels',
      action: this.handleToggleAutoLaunchPanels,
      group: ContextActions.groups.high,
      icon: isAutoLaunchPanelsEnabled ? vsCheck : undefined,
      order: 20
    }, {
      title: 'Close Panels on Disconnect',
      action: this.handleToggleClosePanelsOnDisconnect,
      group: ContextActions.groups.high,
      icon: isClosePanelsOnDisconnectEnabled ? vsCheck : undefined,
      order: 30
    }, {
      title: 'Upload Table from File',
      action: this.handleUploadCsv,
      group: ContextActions.groups.high + 10,
      order: 40
    }];
  }
  handleCommandSubmit(command) {
    if (command === 'clear' || command === 'cls') {
      this.clearConsoleHistory();
    } else if (command.length > 0) {
      // Result is handled in this.handleCommandStarted
      var session = this.props.session;
      session.runCode(command).catch(error => {
        log.error('There was an error initiating the command', error);
      });
    } else {
      // Empty command, just pump a blank line out to history
      var historyItem = {
        command: ' ',
        result: {},
        startTime: Date.now(),
        endTime: Date.now()
      };
      this.scrollConsoleHistoryToBottom();
      this.setState(state => ({
        consoleHistory: state.consoleHistory.concat(historyItem)
      }));
    }
  }
  clearConsoleHistory() {
    this.pending.cancel();
    this.setState(state => {
      // Replace all values with -1 to indicate that table references are still active
      // but don't have corresponding command indexes in consoleHistory
      var objectHistoryMap = new Map([...state.objectHistoryMap.keys()].map(name => [name, -1]));
      return {
        consoleHistory: [],
        objectHistoryMap
      };
    });
  }
  addCommand(command) {
    var focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var execute = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (!this.consoleInput.current) {
      return;
    }
    this.consoleInput.current.setConsoleText(command, focus, execute);
    if (focus) {
      this.scrollConsoleHistoryToBottom();
    }
  }
  focus() {
    var _this$consoleInput$cu2;
    (_this$consoleInput$cu2 = this.consoleInput.current) === null || _this$consoleInput$cu2 === void 0 || _this$consoleInput$cu2.focus();
  }
  sendSettingsChange(prevState, state) {
    var checkIfChanged = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var keys = Object.keys(DEFAULT_SETTINGS);
    var settings = {};
    var hasChanges = false;
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var setting = state[key];
      var prevSetting = prevState[key];
      if (setting !== prevSetting) {
        hasChanges = true;
      }
      settings[key] = setting;
    }
    if (checkIfChanged && !hasChanges) {
      return;
    }
    var onSettingsChange = this.props.onSettingsChange;
    onSettingsChange(settings);
  }
  updateDimensions() {
    if (this.consolePane.current) {
      this.setState({
        consoleHeight: this.consolePane.current.clientHeight
      });
    }
    if (this.consoleInput.current) {
      this.consoleInput.current.updateDimensions();
    }
  }
  render() {
    var _this$props7 = this.props,
      actions = _this$props7.actions,
      dh = _this$props7.dh,
      historyChildren = _this$props7.historyChildren,
      language = _this$props7.language,
      statusBarChildren = _this$props7.statusBarChildren,
      openObject = _this$props7.openObject,
      session = _this$props7.session,
      scope = _this$props7.scope,
      commandHistoryStorage = _this$props7.commandHistoryStorage,
      timeZone = _this$props7.timeZone,
      disabled = _this$props7.disabled,
      unzip = _this$props7.unzip,
      supportsType = _this$props7.supportsType,
      iconForType = _this$props7.iconForType,
      showObjectsMenu = _this$props7.showObjectsMenu;
    var _this$state3 = this.state,
      consoleHeight = _this$state3.consoleHeight,
      consoleHistory = _this$state3.consoleHistory,
      isScrollDecorationShown = _this$state3.isScrollDecorationShown,
      objectMap = _this$state3.objectMap,
      showCsvOverlay = _this$state3.showCsvOverlay,
      csvFile = _this$state3.csvFile,
      csvPaste = _this$state3.csvPaste,
      dragError = _this$state3.dragError,
      csvUploadInProgress = _this$state3.csvUploadInProgress;
    var consoleMenuObjects = this.getObjects(objectMap);
    var inputMaxHeight = Math.round(consoleHeight * 0.7);
    var contextActions = this.getContextActions(actions);
    return /*#__PURE__*/_jsxs("div", {
      role: "presentation",
      className: classNames('iris-console', 'h-100', 'w-100', {
        disabled
      }),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "console-pane",
        ref: this.consolePane,
        children: [/*#__PURE__*/_jsxs(ConsoleStatusBar, {
          dh: dh,
          session: session,
          overflowActions: this.handleOverflowActions,
          children: [statusBarChildren, showObjectsMenu === true && /*#__PURE__*/_jsx(ConsoleObjectsMenu, {
            openObject: openObject,
            objects: consoleMenuObjects
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "console-csv-container",
          onDragOver: CsvOverlay.handleDragOver,
          onDragEnter: this.handleDragEnter,
          onDragLeave: this.handleDragLeave,
          children: [/*#__PURE__*/_jsxs("div", {
            role: "presentation",
            className: classNames('scroll-pane no-scroll-x', {
              'scroll-decoration': isScrollDecorationShown
            }),
            onScroll: this.handleScrollPaneScroll,
            ref: this.consoleHistoryScrollPane,
            children: [/*#__PURE__*/_jsx(ConsoleHistory, {
              items: consoleHistory,
              openObject: openObject,
              language: language,
              disabled: disabled,
              supportsType: supportsType,
              iconForType: iconForType,
              ref: this.consoleHistoryContent,
              onCommandSubmit: this.handleCommandSubmit
            }), historyChildren]
          }), showCsvOverlay && /*#__PURE__*/_jsx(CsvOverlay, {
            onCancel: this.handleCsvFileCanceled,
            onFileOpened: this.handleCsvFileOpened,
            onPaste: this.handleCsvPaste,
            clearDragError: this.handleClearDragError,
            dragError: dragError,
            onError: this.handleCsvError,
            uploadInProgress: csvUploadInProgress,
            allowZip: unzip != null
          })]
        }), !showCsvOverlay && /*#__PURE__*/_jsx(ConsoleInput, {
          ref: this.consoleInput,
          session: session,
          language: language,
          scope: scope,
          onSubmit: this.handleCommandSubmit,
          maxHeight: inputMaxHeight,
          commandHistoryStorage: commandHistoryStorage
        }), showCsvOverlay && /*#__PURE__*/_jsx(CsvInputBar, {
          session: session,
          onOpenTable: this.handleOpenCsvTable,
          onClose: this.handleHideCsv,
          onUpdate: this.handleCsvUpdate,
          onError: this.handleCsvError,
          file: csvFile !== null && csvFile !== void 0 ? csvFile : undefined,
          paste: csvPaste !== null && csvPaste !== void 0 ? csvPaste : undefined,
          onInProgress: this.handleCsvInProgress,
          timeZone: timeZone,
          unzip: unzip
        })]
      }), /*#__PURE__*/_jsx(ContextActions, {
        actions: contextActions,
        ignoreClassNames: [ConsoleInput.INPUT_CLASS_NAME]
      })]
    });
  }
}
_defineProperty(Console, "defaultProps", {
  statusBarChildren: null,
  settings: {},
  onSettingsChange: () => undefined,
  scope: null,
  actions: [],
  historyChildren: null,
  timeZone: 'America/New_York',
  objectMap: new Map(),
  disabled: false,
  unzip: null,
  supportsType: defaultSupportsType,
  iconForType: defaultIconForType,
  showObjectsMenu: true
});
_defineProperty(Console, "LOG_THROTTLE", 500);
export default Console;
//# sourceMappingURL=Console.js.map